// Data Science for Finance & FMAA - Complete Course Data
// Enhanced with Bridgewater-inspired framework, 8 comprehensive stages
// Rich educational content: Python → Pandas → Statistics → Visualization → Financial Modeling → ML → Macro → Portfolio Construction

export const DATA_SCIENCE_FINANCE_COURSE = {
  id: 'data-science-finance',
  title: 'Data Science for Finance & FMAA',
  subtitle: 'A Personal Learning Roadmap',
  description: 'From Python basics to systematic portfolio construction — become a quantitative financial analyst with AI tutoring built in.',
  provider: 'StudyCoach Academy \u00D7 HikariGrove',
  year: '2026',
  goal: 'Quantitative Financial Analyst',
  totalDuration: '18 months',
  dailyCommitment: '30\u201360 minutes',
  stats: {
    stages: 8,
    topics: '50+',
    monthsToMastery: '18',
    careerPotential: '\u221E'
  },
  tags: [
    { label: '8 stages', variant: 'default' },
    { label: '50+ topics', variant: 'default' },
    { label: 'Free', variant: 'success' }
  ],
  isLive: true,

  // ═══════════════════════════════════════════════════════════
  // CURRICULUM \u2014 8 Stages with Rich Lesson Content
  // ═══════════════════════════════════════════════════════════
  curriculum: [

    // ─────────────────────────────────────────────────────────
    // STAGE 1 \u2014 Python Foundations for Finance
    // ─────────────────────────────────────────────────────────
    {
      id: 1,
      stage: 'Stage 1 \u2014 Foundation',
      title: 'Python Foundations for Finance',
      duration: 'Weeks 1\u20136',
      dailyCommitment: '30 minutes',
      focus: 'Essential Python toolkit for financial professionals',
      philosophy: 'Every quant desk in the world runs on Python. Before you can model markets, forecast revenue, or build trading systems, you need fluency in the language that powers modern finance.',
      color: '#00C2A8',
      certification: 'PCEP \u2014 Python Entry Level Programmer',
      lessons: [
        {
          icon: '\uD83D\uDC0D',
          title: 'Python Basics: Variables, Data Types & Operations',
          desc: 'The building blocks of every Python program. Variables store data, data types define what operations you can perform.',
          tags: ['Core Concept', 'Syntax'],
          content: {
            overview: 'Python is the language of choice for data science and finance because of its readability and powerful libraries. Every quantitative desk at Goldman Sachs, Citadel, and Bridgewater runs Python as their primary analysis language. Before diving into pandas and machine learning, you need to master the fundamentals: how data is stored, manipulated, and transformed. Understanding types isn\'t academic \u2014 a float vs int error in a pricing model can mean millions in miscalculated risk.',
            keyConceptsList: [
              'Variables are containers that store data values \u2014 use descriptive names like stock_price, not sp',
              'Python has dynamic typing \u2014 you don\'t declare types explicitly, but wrong types cause subtle bugs',
              'Basic types: int (shares), float (prices), str (tickers), bool (is_profitable), list (portfolio), dict (stock_data)',
              'Operators: arithmetic (+, -, *, /, **), comparison (==, !=, <, >), logical (and, or, not)',
              'f-strings are the modern way to format financial output: f"${price:,.2f}"'
            ],
            codeExample: `# Financial variables
stock_price = 150.25          # float
shares_owned = 100            # int
ticker = "AAPL"               # string
is_profitable = True          # boolean

# Calculate portfolio value
portfolio_value = stock_price * shares_owned
print(f"Portfolio value: \${portfolio_value:,.2f}")

# Lists for multiple stocks
portfolio = ["AAPL", "GOOGL", "MSFT", "AMZN"]
prices = [150.25, 140.50, 380.00, 178.25]

# Dictionary for structured data
stock_data = {
    "ticker": "AAPL",
    "price": 150.25,
    "pe_ratio": 28.5,
    "dividend_yield": 0.52
}

# Total portfolio value using zip
total = sum(p * 100 for p in prices)
print(f"Total portfolio: \${total:,.2f}")`,
            financeApplication: 'Every financial model starts with storing data in variables. Stock prices as floats, share counts as ints, tickers as strings. Dictionaries are how real trading systems organize data by ticker. The code example above is essentially how a simple portfolio tracker works at every brokerage.',
            keyTakeaways: [
              'Use descriptive variable names (stock_price not sp) \u2014 your future self will thank you',
              'Floats for prices, ints for quantities, dicts for structured records',
              'f-strings make financial formatting effortless: f"${value:,.2f}"',
              'Lists + list comprehensions are the backbone of batch financial calculations'
            ],
            resources: [
              { title: 'Python for Everybody (Dr. Chuck)', url: 'https://www.py4e.com/', type: 'course' },
              { title: 'Automate the Boring Stuff with Python', url: 'https://automatetheboringstuff.com/', type: 'book' }
            ]
          }
        },
        {
          icon: '\uD83D\uDCD3',
          title: 'Jupyter Notebooks: Your Financial Lab',
          desc: 'The interactive environment where data scientists and quants do their work. Mix code, visualizations, and documentation.',
          tags: ['Environment', 'Tools'],
          content: {
            overview: 'Jupyter Notebooks are the standard tool for data exploration in finance. Unlike regular Python scripts, notebooks let you run code in cells, see outputs immediately, and create beautiful reports mixing code, charts, and markdown explanations. At hedge funds and banks, Jupyter is where research happens \u2014 from exploring market data to prototyping trading signals. Google Colab gives you a free cloud-based Jupyter environment with GPU access.',
            keyConceptsList: [
              'Cells can contain code or markdown (documentation)',
              'Shift+Enter runs a cell and moves to the next',
              'Variables persist between cells in the same session \u2014 order matters',
              'Perfect for exploratory data analysis (EDA) and rapid prototyping',
              'Export to HTML/PDF for sharing with stakeholders and portfolio managers'
            ],
            codeExample: `# Cell 1: Import and load data
import pandas as pd
import numpy as np

# Cell 2: Quick analysis
prices = pd.Series([150.25, 148.50, 152.00, 149.75, 155.30],
                    index=pd.date_range('2024-01-01', periods=5))

# Cell 3: Calculate returns
returns = prices.pct_change().dropna()
print(f"Mean daily return: {returns.mean():.4%}")
print(f"Volatility: {returns.std():.4%}")

# Cell 4: Markdown documentation
# ## Analysis Summary
# - Stock showed positive trend
# - Annualized volatility: {returns.std() * np.sqrt(252):.2%}`,
            financeApplication: 'Every quant research team uses notebooks for exploratory analysis. The cell-by-cell execution model is perfect for the iterative nature of financial research: load data, explore, transform, model, visualize \u2014 all in one document that tells a complete story.',
            keyTakeaways: [
              'Jupyter is the standard environment for financial data exploration',
              'Cell-based execution enables iterative research workflows',
              'Mix code, charts, and markdown for reproducible research reports',
              'Google Colab provides free cloud Jupyter with no setup required'
            ],
            resources: [
              { title: 'Jupyter Documentation', url: 'https://jupyter.org/documentation', type: 'docs' },
              { title: 'Google Colab (Free Cloud)', url: 'https://colab.research.google.com/', type: 'tool' }
            ]
          }
        },
        {
          icon: '\uD83E\uDDEE',
          title: 'NumPy & Financial Math in Python',
          desc: 'Vectorized financial mathematics: compound interest, present value, future value, NPV, IRR \u2014 the foundations of finance, automated.',
          tags: ['Math', 'NumPy', 'Time Value of Money'],
          content: {
            overview: 'NumPy is the numerical computing engine underneath every Python financial library. It enables vectorized operations \u2014 performing calculations on entire arrays at once rather than looping, which is 50\u2013100\u00D7 faster. In finance, this means calculating returns for 10,000 stocks simultaneously, running Monte Carlo simulations with millions of paths, or computing a full amortization schedule in microseconds. Bridgewater\'s framework insists on always modelling real after-tax returns \u2014 nominal returns are "flattering fiction."',
            keyConceptsList: [
              'NumPy arrays enable vectorized math \u2014 50\u2013100\u00D7 faster than Python loops',
              'Compound interest: A = P(1 + r/n)^(nt) \u2014 the most important equation in finance',
              'Present Value: PV = FV / (1 + r)^n \u2014 a dollar today is worth more than a dollar tomorrow',
              'NPV = sum of discounted future cash flows \u2014 the foundation of all valuation',
              'IRR = the discount rate that makes NPV = 0 \u2014 the rate of return an investment actually delivers',
              'Always model real after-tax returns, not nominal \u2014 inflation and taxes change everything'
            ],
            codeExample: `import numpy as np
import pandas as pd

def compound_engine(principal, rate, years,
                    compound_freq=12, inflation=0.03, tax_rate=0.25):
    """
    Bridgewater-style: always model real after-tax returns.
    Nominal returns are flattering fiction.
    """
    r = rate / compound_freq
    schedule = []
    for yr in range(1, years + 1):
        n = yr * compound_freq
        nom = principal * (1 + r)**n
        after_tax = principal + ((nom - principal) * (1 - tax_rate))
        real = after_tax / (1 + inflation)**yr
        schedule.append({
            'Year': yr,
            'Nominal': round(nom, 2),
            'After-Tax': round(after_tax, 2),
            'Real (Infl-Adj)': round(real, 2)
        })

    df = pd.DataFrame(schedule)
    final_real = df['Real (Infl-Adj)'].iloc[-1]
    nominal_final = df['Nominal'].iloc[-1]
    print(f"CAGR nominal: {((nominal_final/principal)**(1/years)-1)*100:.2f}%")
    print(f"CAGR real after-tax: {((final_real/principal)**(1/years)-1)*100:.2f}%")
    return df

# 100K at 8% for 20 years
compound_engine(100_000, 0.08, 20)`,
            financeApplication: 'Every DCF model, loan calculator, portfolio optimizer, and risk engine uses these building blocks. NumPy\'s vectorization makes it possible to run thousands of scenario analyses in seconds \u2014 what would take hours in Excel.',
            keyTakeaways: [
              'NumPy vectorization is 50\u2013100\u00D7 faster than Python loops \u2014 essential for finance-scale data',
              'Compound interest is the most powerful force in investing \u2014 model it accurately',
              'Always compute real after-tax returns, not nominal \u2014 the difference is enormous over decades',
              'NPV and IRR are the foundation of every investment decision in corporate finance'
            ],
            resources: [
              { title: 'MIT OCW Finance Theory', url: 'https://ocw.mit.edu/courses/15-401-finance-theory-i-fall-2008/', type: 'course' },
              { title: 'numpy-financial library', url: 'https://numpy.org/numpy-financial/', type: 'library' }
            ]
          }
        },
        {
          icon: '\uD83D\uDCC2',
          title: 'Reading & Writing Financial Data',
          desc: 'CSV files, Excel exports, APIs \u2014 loading real financial data into Python for analysis.',
          tags: ['Data I/O', 'Files', 'APIs'],
          content: {
            overview: 'Data is the lifeblood of finance. Before any analysis, you need to load data from the real world: CSV exports from Bloomberg, Excel sheets from accounting, JSON from APIs like Yahoo Finance or FRED. The yfinance library gives you free access to decades of market data. Mastering data I/O is the first step to replacing manual Excel workflows with automated Python pipelines.',
            keyConceptsList: [
              'pd.read_csv() \u2014 the most common data loading function in finance',
              'pd.read_excel() \u2014 essential for working with accounting and corporate data',
              'yfinance library provides free historical market data for any ticker',
              'FRED API gives access to macroeconomic data (GDP, CPI, interest rates)',
              'Always inspect data immediately after loading: .head(), .info(), .describe()'
            ],
            codeExample: `import pandas as pd

# Read CSV from Bloomberg export
# prices = pd.read_csv('stock_prices.csv', parse_dates=['Date'])

# yfinance - free market data
# import yfinance as yf
# aapl = yf.download('AAPL', start='2020-01-01')

# Read Excel with multiple sheets
# financials = pd.read_excel('company.xlsx', sheet_name='Income')

# Simulated example
dates = pd.date_range('2024-01-01', periods=252, freq='B')
import numpy as np
prices = pd.DataFrame({
    'AAPL': 150 * np.cumprod(1 + np.random.normal(0.0004, 0.015, 252)),
    'MSFT': 380 * np.cumprod(1 + np.random.normal(0.0003, 0.012, 252)),
}, index=dates)

print(prices.head())
print(f"\\nShape: {prices.shape}")
print(f"Columns: {list(prices.columns)}")`,
            financeApplication: 'Every analysis begins with data loading. Professional analysts load Bloomberg data, SEC filings, market feeds, and alternative data \u2014 all through Python I/O functions. Automating this step eliminates the copy-paste errors that plague Excel-based workflows.',
            keyTakeaways: [
              'pd.read_csv() and pd.read_excel() handle 90% of financial data loading',
              'yfinance gives free historical market data \u2014 start using it immediately',
              'Always inspect loaded data with .head(), .info(), .describe() before analysis',
              'Automate data loading to eliminate manual errors and save hours weekly'
            ],
            resources: [
              { title: 'yfinance Documentation', url: 'https://pypi.org/project/yfinance/', type: 'library' },
              { title: 'FRED Economic Data', url: 'https://fred.stlouisfed.org/', type: 'data' }
            ]
          }
        },
        {
          icon: '\uD83D\uDD04',
          title: 'Control Flow & Error Handling',
          desc: 'Loops, conditionals, functions, and defensive coding \u2014 essential for building production-quality financial models.',
          tags: ['Logic', 'Functions', 'Error Handling'],
          content: {
            overview: 'Control flow is what turns Python from a calculator into a decision engine. If-else statements make trading decisions. Loops process thousands of securities. Functions encapsulate reusable logic. Error handling ensures your model doesn\'t crash at 3am when the data feed sends unexpected values. In production finance code, defensive programming isn\'t optional \u2014 it\'s survival.',
            keyConceptsList: [
              'if/elif/else for decision logic: buy/sell/hold signals',
              'for loops process batches: all stocks in a universe, all months in a backtest',
              'List comprehensions: Pythonic, fast, readable batch operations',
              'Functions (def) encapsulate reusable logic \u2014 DRY principle',
              'try/except blocks prevent crashes from bad data or API failures',
              'assert statements validate assumptions in financial models'
            ],
            codeExample: `# Trading signal generator with error handling
def screen_stocks(stocks, min_pe=0, max_pe=25, min_div=2.0):
    """Screen stocks by P/E ratio and dividend yield."""
    try:
        results = []
        for stock in stocks:
            if stock['pe_ratio'] is None:
                continue  # Skip missing data
            if min_pe <= stock['pe_ratio'] <= max_pe:
                if stock.get('dividend_yield', 0) >= min_div:
                    results.append(stock)
        return sorted(results, key=lambda x: x['pe_ratio'])
    except (KeyError, TypeError) as e:
        print(f"Data error: {e}")
        return []

# Sample universe
universe = [
    {'ticker': 'JNJ', 'pe_ratio': 15.2, 'dividend_yield': 3.1},
    {'ticker': 'NVDA', 'pe_ratio': 65.0, 'dividend_yield': 0.03},
    {'ticker': 'KO', 'pe_ratio': 22.8, 'dividend_yield': 3.2},
    {'ticker': 'AAPL', 'pe_ratio': 28.5, 'dividend_yield': 0.5},
]

value_stocks = screen_stocks(universe)
for s in value_stocks:
    print(f"{s['ticker']}: P/E {s['pe_ratio']}, Yield {s['dividend_yield']}%")`,
            financeApplication: 'Every screening tool, backtesting engine, and risk management system uses control flow extensively. The stock screener above is a simplified version of what Bloomberg\'s screening function does \u2014 filtering a universe of securities against multiple criteria.',
            keyTakeaways: [
              'Conditionals drive trading decisions; loops process entire security universes',
              'Functions make financial logic reusable and testable',
              'Error handling is non-negotiable in production finance code',
              'List comprehensions are the Pythonic way to process financial data in batch'
            ],
            resources: [
              { title: 'Python Control Flow', url: 'https://docs.python.org/3/tutorial/controlflow.html', type: 'docs' },
              { title: 'Real Python \u2014 Error Handling', url: 'https://realpython.com/python-exceptions/', type: 'tutorial' }
            ]
          }
        }
      ],
      practiceProjects: [
        'Build a compound interest calculator with inflation adjustment',
        'Create a loan amortization schedule generator',
        'Write a stock screener that filters by P/E, dividend yield, and market cap'
      ]
    },

    // ─────────────────────────────────────────────────────────
    // STAGE 2 \u2014 Data Manipulation with Pandas
    // ─────────────────────────────────────────────────────────
    {
      id: 2,
      stage: 'Stage 2 \u2014 Data Engineering',
      title: 'Data Manipulation with Pandas',
      duration: 'Weeks 7\u201312',
      dailyCommitment: '30 minutes',
      focus: 'Mastering Pandas for financial data at professional scale',
      philosophy: 'Pandas is to the financial analyst what Excel is to the accountant \u2014 except it handles millions of rows, never corrupts formulas, and automates everything. Every data pipeline at every quant fund runs through Pandas.',
      color: '#3B82F6',
      certification: 'PCAP \u2014 Python Associate',
      lessons: [
        {
          icon: '\uD83D\uDCCA',
          title: 'DataFrames & Series: The Core of Pandas',
          desc: 'The two fundamental data structures that power every financial analysis in Python.',
          tags: ['Pandas', 'Core Concept'],
          content: {
            overview: 'A DataFrame is a 2D table \u2014 think of it as a supercharged Excel spreadsheet that can handle millions of rows without breaking. A Series is a single column. Together, they\'re the primitives that every financial data pipeline is built on. Unlike Excel, Pandas never corrupts formulas, handles missing data gracefully, and processes in milliseconds what takes Excel minutes.',
            keyConceptsList: [
              'DataFrame = 2D table (rows and columns), like a spreadsheet but infinitely more powerful',
              'Series = single column, essentially a labelled array',
              'Create from dicts, lists, CSV files, Excel, or database queries',
              '.shape, .dtypes, .describe() \u2014 always inspect your data immediately',
              'Column access: df["column"] or df.column \u2014 returns a Series',
              'Indexing is critical: DatetimeIndex enables time series operations'
            ],
            codeExample: `import pandas as pd
import numpy as np

# Create from dict (most common in finance)
portfolio = pd.DataFrame({
    'Ticker': ['AAPL', 'MSFT', 'GOOGL', 'AMZN', 'JNJ'],
    'Shares': [100, 50, 30, 40, 80],
    'Price': [185.50, 415.25, 175.80, 195.30, 155.75],
    'Sector': ['Tech', 'Tech', 'Tech', 'Tech', 'Healthcare']
})

# Calculated columns
portfolio['Value'] = portfolio['Shares'] * portfolio['Price']
portfolio['Weight'] = portfolio['Value'] / portfolio['Value'].sum()

# Quick analysis
print(portfolio.to_string(index=False))
print(f"\\nTotal value: \${portfolio['Value'].sum():,.2f}")
print(f"\\nSector breakdown:")
print(portfolio.groupby('Sector')['Weight'].sum().apply(lambda x: f"{x:.1%}"))`,
            financeApplication: 'DataFrames are how every hedge fund stores position data, every bank stores transaction records, and every analyst stores financial statements. The portfolio tracking code above is essentially what runs at the core of every portfolio management system.',
            keyTakeaways: [
              'DataFrame is the core data structure for all financial analysis in Python',
              'Always create calculated columns (Value, Weight) rather than hardcoding',
              '.groupby() is the Pandas equivalent of pivot tables \u2014 essential for sector/asset analysis',
              'Pandas handles millions of rows where Excel crashes at ~1M'
            ],
            resources: [
              { title: 'Pandas Documentation', url: 'https://pandas.pydata.org/docs/', type: 'docs' },
              { title: '10 Minutes to Pandas', url: 'https://pandas.pydata.org/docs/user_guide/10min.html', type: 'tutorial' }
            ]
          }
        },
        {
          icon: '\uD83D\uDD0D',
          title: 'Filtering, Sorting & Selecting Financial Data',
          desc: 'Boolean indexing, .loc/.iloc, conditional filtering \u2014 extracting exactly the data you need.',
          tags: ['Selection', 'Filtering'],
          content: {
            overview: 'The power of Pandas isn\'t just storing data \u2014 it\'s extracting exactly what you need. Boolean indexing lets you filter datasets with conditions: all stocks with P/E below 15, all trades above $1M, all days where the market dropped more than 2%. This is how screening tools, compliance systems, and trading signals work in practice.',
            keyConceptsList: [
              'Boolean indexing: df[df["PE"] < 15] returns all rows matching the condition',
              '.loc[] for label-based selection: df.loc["2024-01-15":"2024-03-01"]',
              '.iloc[] for position-based: df.iloc[0:10] for first 10 rows',
              'Multiple conditions: use & (and), | (or), ~ (not) with parentheses',
              '.query() for readable SQL-like filtering: df.query("PE < 15 and Sector == \'Tech\'")',
              '.nlargest() and .nsmallest() for quick top/bottom rankings'
            ],
            codeExample: `import pandas as pd
import numpy as np

# Stock universe
stocks = pd.DataFrame({
    'Ticker': ['AAPL','MSFT','JNJ','KO','NVDA','PG','XOM','JPM'],
    'Price': [185, 415, 156, 62, 880, 165, 105, 195],
    'PE': [28.5, 35.2, 15.1, 22.8, 65.0, 25.3, 12.4, 11.2],
    'DivYield': [0.5, 0.7, 3.1, 3.2, 0.03, 2.4, 3.5, 2.6],
    'Sector': ['Tech','Tech','Health','Staples','Tech','Staples','Energy','Finance']
})

# Value stocks: P/E < 20 AND dividend > 2%
value = stocks[(stocks['PE'] < 20) & (stocks['DivYield'] > 2)]
print("VALUE SCREEN:")
print(value[['Ticker','PE','DivYield']].to_string(index=False))

# Top 3 by dividend yield
print("\\nTOP DIVIDEND PAYERS:")
print(stocks.nlargest(3, 'DivYield')[['Ticker','DivYield']].to_string(index=False))`,
            financeApplication: 'Every stock screener (Bloomberg, Finviz, custom) uses boolean filtering. Compliance teams filter trades above reporting thresholds. Risk managers filter positions exceeding concentration limits. The value screen above is exactly how Warren Buffett\'s team would filter a universe.',
            keyTakeaways: [
              'Boolean indexing is the most important Pandas operation for financial screening',
              'Use .loc for date-range slicing on time series data',
              'Chain conditions with & and | for multi-criteria screens',
              '.nlargest() and .nsmallest() instantly rank securities by any metric'
            ],
            resources: [
              { title: 'Pandas Indexing Guide', url: 'https://pandas.pydata.org/docs/user_guide/indexing.html', type: 'docs' }
            ]
          }
        },
        {
          icon: '\u23F0',
          title: 'Time Series Operations & Resampling',
          desc: 'DatetimeIndex, resampling daily to monthly, rolling windows, returns calculation \u2014 the heart of financial data.',
          tags: ['Time Series', 'Resampling'],
          content: {
            overview: 'Financial data is fundamentally time series data. Pandas was literally designed for this. DatetimeIndex enables powerful time-based operations: slicing by date range, resampling daily data to monthly, computing rolling averages, and calculating returns. These operations form the backbone of every technical indicator, risk metric, and performance report in finance.',
            keyConceptsList: [
              'DatetimeIndex: pd.to_datetime() converts strings to proper dates',
              '.resample("M") converts daily to monthly; "Q" for quarterly, "A" for annual',
              '.rolling(window) computes moving averages, volatility, and other windowed metrics',
              '.pct_change() calculates returns \u2014 the single most used function in quant finance',
              '.shift() creates lagged values \u2014 essential for avoiding look-ahead bias',
              'Business day frequency (freq="B") excludes weekends automatically'
            ],
            codeExample: `import pandas as pd
import numpy as np

# Simulated daily price data
np.random.seed(42)
dates = pd.date_range('2023-01-01', '2024-12-31', freq='B')
prices = pd.Series(
    100 * np.cumprod(1 + np.random.normal(0.0003, 0.015, len(dates))),
    index=dates, name='Price'
)

# Daily returns
returns = prices.pct_change().dropna()

# Monthly resampling
monthly = prices.resample('M').last()
monthly_returns = monthly.pct_change().dropna()

# Rolling metrics
rolling_vol = returns.rolling(21).std() * np.sqrt(252)  # 21-day annualized vol
rolling_mean = prices.rolling(50).mean()  # 50-day moving average

print(f"Annualized return: {returns.mean() * 252:.2%}")
print(f"Annualized volatility: {returns.std() * np.sqrt(252):.2%}")
print(f"Sharpe ratio: {(returns.mean() * 252) / (returns.std() * np.sqrt(252)):.3f}")
print(f"Max drawdown: {((prices / prices.cummax()) - 1).min():.2%}")`,
            financeApplication: 'Every performance report, risk dashboard, and trading system uses these exact operations. The 50-day moving average is one of the most watched technical indicators in the world. Annualized volatility and Sharpe ratio are the standard metrics reported by every fund.',
            keyTakeaways: [
              '.pct_change() is the most important function in quantitative finance',
              'Resampling converts between frequencies \u2014 daily to monthly is used constantly',
              'Rolling windows compute time-varying statistics like volatility and moving averages',
              'Annualize daily returns by multiplying mean by 252 and std by sqrt(252)'
            ],
            resources: [
              { title: 'Pandas Time Series Guide', url: 'https://pandas.pydata.org/docs/user_guide/timeseries.html', type: 'docs' }
            ]
          }
        },
        {
          icon: '\uD83D\uDD17',
          title: 'Merging, Joining & Data Pipelines',
          desc: 'Combining financial statements, enriching price data with fundamentals, building automated data pipelines.',
          tags: ['Merging', 'Pipelines', 'Method Chaining'],
          content: {
            overview: 'Real financial analysis requires combining data from multiple sources: price data from one feed, fundamentals from another, sector classifications from a third. Pandas merge/join operations are how you assemble these pieces. Method chaining creates readable, maintainable data pipelines that transform raw data into analysis-ready datasets in a single expression.',
            keyConceptsList: [
              'pd.merge() joins two DataFrames on matching keys (like SQL JOIN)',
              'Inner join: only matching rows. Left join: keep all left rows. Outer: keep everything',
              '.concat() stacks DataFrames vertically (adding more rows) or horizontally (more columns)',
              'Method chaining: df.dropna().assign(returns=...).query("returns > 0") \u2014 readable pipelines',
              'Always validate merges: check .shape before and after to catch data loss'
            ],
            codeExample: `import pandas as pd
import numpy as np

# Price data from market feed
prices = pd.DataFrame({
    'Ticker': ['AAPL', 'MSFT', 'JNJ', 'KO'],
    'Price': [185.50, 415.25, 155.75, 62.30],
    'Volume': [52e6, 22e6, 8e6, 12e6]
})

# Fundamentals from separate source
fundamentals = pd.DataFrame({
    'Ticker': ['AAPL', 'MSFT', 'JNJ', 'KO', 'PG'],
    'PE': [28.5, 35.2, 15.1, 22.8, 25.3],
    'ROE': [0.147, 0.39, 0.21, 0.42, 0.29]
})

# Merge and build pipeline
enriched = (
    prices
    .merge(fundamentals, on='Ticker', how='left')
    .assign(
        Earnings=lambda x: x['Price'] / x['PE'],
        ValueScore=lambda x: (1/x['PE'] + x['ROE']) / 2
    )
    .sort_values('ValueScore', ascending=False)
)

print(enriched.to_string(index=False))`,
            financeApplication: 'Portfolio management systems merge position data with market prices every second. Research platforms merge price history with fundamental data for factor models. The method chaining pattern above is how production data pipelines are built at quantitative firms.',
            keyTakeaways: [
              'pd.merge() is essential for combining price data with fundamentals',
              'Method chaining creates clean, readable, maintainable data pipelines',
              'Always validate merges \u2014 check shapes before and after to catch data issues',
              'Left joins preserve your primary dataset even when secondary data is missing'
            ],
            resources: [
              { title: 'Pandas Merge Guide', url: 'https://pandas.pydata.org/docs/user_guide/merging.html', type: 'docs' },
              { title: 'Python for Finance \u2014 Yves Hilpisch', url: 'https://home.tpq.io/books/py4fi/', type: 'book' }
            ]
          }
        },
        {
          icon: '\uD83E\uDDF9',
          title: 'Data Cleaning & Missing Data in Finance',
          desc: 'Handling missing prices, corporate actions, outliers, and dirty accounting data \u2014 the unglamorous reality of financial data.',
          tags: ['Data Quality', 'Missing Data'],
          content: {
            overview: 'Real financial data is messy. Prices have gaps on holidays. Corporate actions (splits, dividends) create discontinuities. Accounting data has missing fields and inconsistent formats. Spending time on data cleaning isn\'t wasted \u2014 it\'s where most analysis errors originate. Professional quants spend 60\u201380% of their time on data cleaning. Getting this right is the difference between a profitable model and a catastrophic one.',
            keyConceptsList: [
              '.isnull() and .notnull() detect missing values across entire DataFrames',
              '.fillna(method="ffill") forward-fills missing prices \u2014 the standard approach for market data',
              '.dropna() removes rows with missing data \u2014 use cautiously in time series',
              '.interpolate() fills gaps with linear interpolation \u2014 better than ffill for some cases',
              'Outlier detection: anything beyond 3 standard deviations deserves investigation',
              'Always document your cleaning decisions \u2014 they affect every downstream analysis'
            ],
            codeExample: `import pandas as pd
import numpy as np

# Simulated messy general ledger
np.random.seed(42)
gl = pd.DataFrame({
    'Date': pd.date_range('2024-01-01', periods=100, freq='B'),
    'Account': np.random.choice(['Revenue','COGS','OpEx','Marketing', None], 100),
    'Amount': np.random.normal(50000, 25000, 100).round(2),
})

print(f"BEFORE: rows={len(gl)}, nulls={gl.isnull().sum().sum()}")

# Clean pipeline
gl_clean = (gl
    .dropna(subset=['Account'])
    .assign(
        Month=lambda x: x['Date'].dt.to_period('M'),
        Amount=lambda x: np.where(
            x['Account'] == 'Revenue',
            abs(x['Amount']),
            -abs(x['Amount'])
        )
    )
)

# Monthly P&L
monthly_pl = gl_clean.groupby(['Month','Account'])['Amount'].sum().unstack(fill_value=0)
print(f"\\nAFTER: rows={len(gl_clean)}, nulls={gl_clean.isnull().sum().sum()}")
print("\\nMONTHLY P&L:")
print(monthly_pl.head().to_string())`,
            financeApplication: 'Every financial data pipeline starts with cleaning. Bloomberg data has gaps. SEC filings have inconsistencies. The general ledger cleaning pipeline above is exactly what accounting automation software does \u2014 turning messy raw data into structured, analysis-ready financial statements.',
            keyTakeaways: [
              'Professional quants spend 60\u201380% of their time on data cleaning \u2014 it\'s not optional',
              'Forward-fill (.ffill()) is the standard for missing market prices',
              'Always check for nulls, outliers, and format inconsistencies before any analysis',
              'Document every cleaning decision \u2014 they directly affect your conclusions'
            ],
            resources: [
              { title: 'Pandas Missing Data', url: 'https://pandas.pydata.org/docs/user_guide/missing_data.html', type: 'docs' }
            ]
          }
        }
      ],
      practiceProjects: [
        'Build a financial data pipeline: load, clean, merge, and analyze a multi-source dataset',
        'Create an automated P&L report from a messy general ledger',
        'Build a stock comparison tool that merges price and fundamental data'
      ]
    },

    // ─────────────────────────────────────────────────────────
    // STAGE 3 \u2014 Statistics & Financial Mathematics
    // ─────────────────────────────────────────────────────────
    {
      id: 3,
      stage: 'Stage 3 \u2014 Statistics',
      title: 'Statistics & Financial Mathematics',
      duration: 'Weeks 13\u201318',
      dailyCommitment: '30 minutes',
      focus: 'The mathematical foundations of every risk model ever built',
      philosophy: 'Bridgewater\'s framework rests on one insight: most people confuse certainty with probability. Statistics is the language of uncertainty \u2014 and finance is fundamentally about managing uncertainty.',
      color: '#8B5CF6',
      certification: 'Google Data Analytics Professional Certificate',
      lessons: [
        {
          icon: '\uD83D\uDCCF',
          title: 'Descriptive Statistics & Risk Metrics',
          desc: 'Mean, standard deviation, skewness, kurtosis, VaR, CVaR \u2014 the metrics that define every portfolio.',
          tags: ['Statistics', 'Risk Metrics'],
          content: {
            overview: 'Descriptive statistics summarize the characteristics of financial return distributions. But in finance, the mean and standard deviation aren\'t enough. Skewness tells you if returns are asymmetric (most market crashes are left-skewed). Kurtosis tells you if extreme events are more likely than a normal distribution predicts (they are \u2014 this is "fat tails"). Value at Risk (VaR) and Conditional VaR (CVaR) quantify the worst-case scenarios that matter most to risk managers.',
            keyConceptsList: [
              'Mean return: the average \u2014 but averages lie when distributions are skewed',
              'Standard deviation (volatility): the spread of returns around the mean',
              'Skewness: negative skew means more extreme losses than gains \u2014 markets are left-skewed',
              'Kurtosis: excess kurtosis > 0 means fatter tails than normal \u2014 crashes are more likely than you think',
              '95% VaR: the loss that won\'t be exceeded 95% of the time \u2014 the standard risk metric',
              'CVaR (Expected Shortfall): the average loss when VaR is breached \u2014 what really hurts'
            ],
            codeExample: `import numpy as np
import pandas as pd
from scipy import stats

np.random.seed(42)

# Simulated portfolio returns
assets = {
    'US_Equity': {'mean': 0.0004, 'std': 0.012},
    'Bonds': {'mean': 0.0001, 'std': 0.004},
    'Gold': {'mean': 0.0002, 'std': 0.009},
    'Commodities': {'mean': 0.0001, 'std': 0.014},
}

n_days = 1260  # 5 years
returns = pd.DataFrame({
    a: np.random.normal(p['mean'], p['std'], n_days)
    for a, p in assets.items()
})

weights = np.array([0.25, 0.25, 0.25, 0.25])
port_r = returns @ weights

ann_r = port_r.mean() * 252
ann_vol = port_r.std() * np.sqrt(252)
sharpe = ann_r / ann_vol
skew = stats.skew(port_r)
kurt = stats.kurtosis(port_r)
var_95 = np.percentile(port_r, 5)
cvar_95 = port_r[port_r <= var_95].mean()

print("PORTFOLIO RISK DASHBOARD")
print("=" * 40)
print(f"Ann. Return:     {ann_r*100:.2f}%")
print(f"Ann. Volatility: {ann_vol*100:.2f}%")
print(f"Sharpe Ratio:    {sharpe:.3f}")
print(f"Skewness:        {skew:.3f}")
print(f"Excess Kurtosis: {kurt:.3f}")
print(f"95% VaR (1-day): {var_95*100:.3f}%")
print(f"95% CVaR:        {cvar_95*100:.3f}%")
print(f"On $10M: VaR = \${abs(var_95)*10e6:,.0f}")`,
            financeApplication: 'Every risk management system at every bank and fund computes these exact metrics daily. The 2008 crisis proved that models assuming normal distributions (ignoring fat tails) catastrophically underestimate risk. The VaR/CVaR framework above is what regulators require banks to compute.',
            keyTakeaways: [
              'Mean and standard deviation aren\'t enough \u2014 skewness and kurtosis reveal hidden risks',
              'Markets have fat tails: extreme events happen far more often than normal distributions predict',
              'VaR is the industry standard risk metric; CVaR captures what happens in the worst scenarios',
              'The 2008 crisis proved: ignoring fat tails in risk models is catastrophically dangerous'
            ],
            resources: [
              { title: 'Quantitative Risk Management', url: 'https://press.princeton.edu/books/hardcover/9780691166278/quantitative-risk-management', type: 'book' },
              { title: 'SciPy Statistics', url: 'https://docs.scipy.org/doc/scipy/reference/stats.html', type: 'docs' }
            ]
          }
        },
        {
          icon: '\uD83D\uDD04',
          title: 'Correlation, Covariance & Diversification',
          desc: 'The mathematical proof that diversification works \u2014 and why it fails exactly when you need it most.',
          tags: ['Correlation', 'Portfolio Theory'],
          content: {
            overview: 'Harry Markowitz won the Nobel Prize for proving that combining assets with low correlations reduces portfolio risk without reducing expected return \u2014 the only "free lunch" in finance. The correlation matrix is the most important input to portfolio construction. But there\'s a dangerous catch: correlations spike during crises. Assets that seemed uncorrelated in calm markets become highly correlated in crashes, exactly when diversification matters most.',
            keyConceptsList: [
              'Correlation ranges from -1 (perfect inverse) to +1 (moves together)',
              'Low correlation between assets = risk reduction without return sacrifice',
              'The correlation matrix is the most important input to portfolio optimization',
              'Correlations are NOT stable \u2014 they spike during crises (correlation breakdown)',
              'Rolling correlation reveals how relationships change over time',
              'Covariance = correlation \u00D7 vol_A \u00D7 vol_B \u2014 the building block of portfolio variance'
            ],
            codeExample: `import numpy as np
import pandas as pd

np.random.seed(42)
n = 1260

# Asset returns with realistic correlations
returns = pd.DataFrame({
    'US_Equity': np.random.normal(0.0004, 0.012, n),
    'Bonds': np.random.normal(0.0001, 0.004, n),
    'Gold': np.random.normal(0.0002, 0.009, n),
    'Real_Estate': np.random.normal(0.0003, 0.010, n),
})

# Correlation matrix
corr = returns.corr()
print("CORRELATION MATRIX:")
print(corr.round(3).to_string())

# Portfolio variance with correlation
w = np.array([0.4, 0.3, 0.15, 0.15])
cov = returns.cov() * 252  # Annualized
port_vol = np.sqrt(w @ cov.values @ w)
weighted_avg_vol = sum(w * returns.std() * np.sqrt(252))

print(f"\\nPortfolio volatility: {port_vol:.2%}")
print(f"Weighted avg vol:     {weighted_avg_vol:.2%}")
print(f"Diversification benefit: {(1 - port_vol/weighted_avg_vol):.1%}")`,
            financeApplication: 'The correlation matrix drives every portfolio optimization at every asset manager. Bridgewater\'s All Weather portfolio is designed specifically to hold assets that behave differently across economic environments \u2014 equities for growth, bonds for deflation, gold for inflation, commodities for supply shocks.',
            keyTakeaways: [
              'Low correlation = the only free lunch in finance (Markowitz\'s insight)',
              'Correlations are unstable \u2014 they spike during crises, reducing diversification when needed most',
              'Rolling correlation reveals how relationships change \u2014 essential for dynamic allocation',
              'Portfolio volatility is always less than the weighted average of individual volatilities'
            ],
            resources: [
              { title: 'Portfolio Theory \u2014 MIT OCW', url: 'https://ocw.mit.edu/courses/15-401-finance-theory-i-fall-2008/', type: 'course' }
            ]
          }
        },
        {
          icon: '\uD83D\uDCC8',
          title: 'Linear Regression & Factor Models',
          desc: 'OLS regression, R-squared, beta \u2014 the foundation of CAPM, Fama-French, and every multi-factor model.',
          tags: ['Regression', 'Factor Models'],
          content: {
            overview: 'Linear regression is the workhorse of finance. CAPM (Capital Asset Pricing Model) is just a single regression: stock return = alpha + beta \u00D7 market return. Fama-French adds size and value factors. Multi-factor models at hedge funds use dozens of factors. Understanding regression means understanding how to decompose returns into systematic (market) and idiosyncratic (stock-specific) components.',
            keyConceptsList: [
              'y = \u03B1 + \u03B2x + \u03B5 \u2014 the most important equation in quantitative finance',
              'Beta (\u03B2): sensitivity to market movements. \u03B2 > 1 means more volatile than market',
              'Alpha (\u03B1): return above what beta explains \u2014 the holy grail of active management',
              'R-squared: how much of return variation the model explains (0 to 1)',
              'Residuals: what the model can\'t explain \u2014 should be random if model is good',
              'Multi-factor models: Fama-French 3-factor, Carhart 4-factor, Barra multi-factor'
            ],
            codeExample: `import numpy as np
import pandas as pd
from scipy import stats

np.random.seed(42)
n = 252

# Simulated market and stock returns
market = np.random.normal(0.0004, 0.01, n)
stock = 0.0002 + 1.3 * market + np.random.normal(0, 0.008, n)

# Run regression (CAPM)
slope, intercept, r_value, p_value, std_err = stats.linregress(market, stock)

print("CAPM REGRESSION RESULTS")
print("=" * 40)
print(f"Alpha (daily):  {intercept:.6f}")
print(f"Alpha (annual): {intercept * 252 * 100:.2f}%")
print(f"Beta:           {slope:.3f}")
print(f"R-squared:      {r_value**2:.3f}")
print(f"P-value:        {p_value:.6f}")
print(f"\\nInterpretation:")
print(f"  This stock moves {slope:.1f}x the market")
print(f"  {r_value**2*100:.0f}% of variance explained by market")
print(f"  Alpha is {'significant' if p_value < 0.05 else 'not significant'}")`,
            financeApplication: 'Every performance attribution report decomposes returns using regression. Portfolio managers use beta to hedge market exposure. Factor models determine which risks a portfolio is exposed to. Alpha \u2014 the intercept \u2014 is literally what active managers are paid to generate.',
            keyTakeaways: [
              'CAPM is a single regression: stock return = alpha + beta \u00D7 market return',
              'Beta measures market sensitivity; alpha measures skill (or luck)',
              'R-squared tells you how much the model explains \u2014 low R\u00B2 means other factors matter',
              'Always check statistical significance \u2014 most "alpha" disappears when properly tested'
            ],
            resources: [
              { title: 'Statsmodels Documentation', url: 'https://www.statsmodels.org/', type: 'library' }
            ]
          }
        },
        {
          icon: '\uD83C\uDFB2',
          title: 'Monte Carlo Simulation',
          desc: 'The engine of modern risk management \u2014 simulating thousands of possible futures to quantify uncertainty.',
          tags: ['Simulation', 'Risk', 'Advanced'],
          content: {
            overview: 'Monte Carlo simulation generates thousands of random future scenarios based on historical statistics. Instead of a single forecast ("the stock will return 10%"), you get a probability distribution of outcomes. This is how banks stress-test portfolios, options are priced, and retirement planners estimate the probability of running out of money. It\'s the single most powerful technique in risk management.',
            keyConceptsList: [
              'Generate thousands of random paths using historical mean and volatility',
              'Each path represents one possible future \u2014 the distribution of endpoints is the forecast',
              'Percentiles give confidence intervals: "95% chance the portfolio stays above $X"',
              'Used for: option pricing, portfolio risk, retirement planning, project finance',
              'More paths = more stable estimates. 10,000 is a common starting point'
            ],
            codeExample: `import numpy as np

np.random.seed(42)

# Portfolio Monte Carlo
initial = 1_000_000
daily_mean = 0.0003
daily_vol = 0.012
days = 252  # 1 year
n_sims = 10_000

# Generate paths
daily_returns = np.random.normal(daily_mean, daily_vol, (days, n_sims))
paths = initial * np.cumprod(1 + daily_returns, axis=0)
final_values = paths[-1]

print("MONTE CARLO: $1M PORTFOLIO, 1 YEAR")
print("=" * 42)
for pct in [5, 25, 50, 75, 95]:
    val = np.percentile(final_values, pct)
    ret = (val / initial - 1) * 100
    print(f"  P{pct:2d}: \${val:>12,.0f}  ({ret:+.1f}%)")

print(f"\\n  P(loss > 20%): {(final_values < 800_000).mean()*100:.1f}%")
print(f"  P(gain > 20%): {(final_values > 1_200_000).mean()*100:.1f}%")
print(f"  Expected value: \${final_values.mean():,.0f}")`,
            financeApplication: 'Banks use Monte Carlo for regulatory capital calculations. Options desks use it for pricing exotic derivatives. Financial planners use it to answer "What\'s the probability I run out of money in retirement?" It replaces the false precision of single-point forecasts with honest probability distributions.',
            keyTakeaways: [
              'Monte Carlo replaces false precision with honest probability distributions',
              'Generate thousands of scenarios from historical statistics to quantify uncertainty',
              'Percentiles give actionable risk metrics: "95% chance portfolio stays above $X"',
              'This is how every bank, fund, and insurance company models risk in practice'
            ],
            resources: [
              { title: 'Options, Futures and Other Derivatives \u2014 Hull', url: 'https://www.pearson.com/en-us/subject-catalog/p/options-futures-and-other-derivatives/P200000005938', type: 'book' }
            ]
          }
        }
      ],
      practiceProjects: [
        'Build a portfolio risk dashboard with VaR, CVaR, Sharpe Ratio, and fat tail analysis',
        'Run a Monte Carlo simulation on a $1M retirement portfolio over 30 years',
        'Compute CAPM beta and alpha for 10 stocks against the S&P 500'
      ]
    },

    // ─────────────────────────────────────────────────────────
    // STAGE 4 \u2014 Data Visualization for Finance
    // ─────────────────────────────────────────────────────────
    {
      id: 4,
      stage: 'Stage 4 \u2014 Visualization',
      title: 'Data Visualization for Finance',
      duration: 'Weeks 19\u201324',
      dailyCommitment: '30 minutes',
      focus: 'Communicating financial insights that drive decisions',
      philosophy: 'A bad chart destroys good analysis. The best investors are also the best communicators. Visualization is not decoration \u2014 it is the primary interface between analysis and decision-making.',
      color: '#F59E0B',
      certification: 'IBM Data Science Professional Certificate',
      lessons: [
        {
          icon: '\uD83D\uDCCA',
          title: 'Matplotlib: The Foundation',
          desc: 'The base charting library that every other visualization tool builds on. Master it and you can create anything.',
          tags: ['Matplotlib', 'Charts'],
          content: {
            overview: 'Matplotlib is the foundation every Python chart library builds on. While Plotly and Seaborn are shinier, understanding Matplotlib gives you complete control over every pixel in your chart. In finance, this matters: you need precise axis labels, dual y-axes for price/volume, and publication-quality formatting for research reports and investor decks.',
            keyConceptsList: [
              'fig, ax = plt.subplots() \u2014 the object-oriented API gives full control',
              'Line charts for price/return time series \u2014 the most common financial chart',
              'Bar charts for comparing metrics across categories (sectors, periods)',
              'Dual y-axes for price + volume or price + indicator overlays',
              'Customization: colours, fonts, gridlines, legends, annotations',
              'plt.savefig() exports publication-quality PDFs for reports'
            ],
            codeExample: `import numpy as np

# Note: In a Jupyter notebook, you'd use matplotlib:
# import matplotlib.pyplot as plt
#
# fig, (ax1, ax2) = plt.subplots(2, 1, figsize=(12, 8))
#
# # Price chart with moving average
# ax1.plot(dates, prices, label='Price', color='#3B82F6')
# ax1.plot(dates, ma_50, label='50-day MA', color='#F59E0B')
# ax1.set_title('Stock Price with 50-Day Moving Average')
# ax1.legend()
#
# # Returns histogram
# ax2.hist(returns, bins=50, color='#8B5CF6', alpha=0.7)
# ax2.axvline(var_95, color='red', linestyle='--', label='95% VaR')
# ax2.set_title('Return Distribution with VaR')
# ax2.legend()
#
# plt.tight_layout()
# plt.savefig('analysis.pdf', dpi=300)

# The key principle: every chart needs
# 1. Clear title stating the insight
# 2. Labeled axes with units
# 3. Legend if multiple series
# 4. Minimal chartjunk (no 3D, no gradients)
print("Chart design principles:")
print("1. Title states the insight, not the data")
print("2. Axes labeled with units (%, $, date)")
print("3. Remove chartjunk: no 3D, no gradients")
print("4. Color with purpose: red=danger, green=good")`,
            financeApplication: 'Every research report, investor presentation, and risk dashboard is built with these charting fundamentals. Goldman Sachs research uses clean, minimal charts. The dual y-axis pattern (price + volume) is the standard financial chart layout used by Bloomberg and Reuters.',
            keyTakeaways: [
              'Use the object-oriented API (fig, ax) for full control over financial charts',
              'Every chart needs: clear title, labeled axes, legend, minimal decoration',
              'Dual y-axes are essential for overlaying price with volume or indicators',
              'Export as PDF for publication-quality research reports'
            ],
            resources: [
              { title: 'Matplotlib Documentation', url: 'https://matplotlib.org/', type: 'docs' },
              { title: 'Storytelling with Data', url: 'https://www.storytellingwithdata.com/', type: 'book' }
            ]
          }
        },
        {
          icon: '\uD83D\uDD25',
          title: 'Plotly: Interactive Financial Charts',
          desc: 'Publication-quality interactive charts: candlesticks, correlation heatmaps, dashboards with hover tooltips.',
          tags: ['Plotly', 'Interactive', 'Dashboards'],
          content: {
            overview: 'Plotly creates interactive, web-ready charts that can be embedded in dashboards, shared as HTML, or deployed in Streamlit apps. For finance, Plotly excels at candlestick charts, correlation heatmaps, and multi-panel dashboards. The interactivity \u2014 hover tooltips showing exact values, zoom, pan \u2014 makes it the preferred tool for portfolio dashboards.',
            keyConceptsList: [
              'Plotly Express: one-line charts for rapid exploration',
              'go.Candlestick() \u2014 the standard chart for price data (OHLC)',
              'Correlation heatmaps: px.imshow(corr_matrix) for factor analysis',
              'make_subplots() for multi-panel dashboards',
              'Hover tooltips show exact values without cluttering the chart',
              'Export as interactive HTML or static images for reports'
            ],
            codeExample: `# Plotly creates interactive charts
# In Jupyter:
#
# import plotly.graph_objects as go
# from plotly.subplots import make_subplots
#
# fig = make_subplots(rows=2, cols=2,
#     subplot_titles=('Asset Performance', 'Yield Curves',
#                     'Rolling Correlation', 'Annual Returns'))
#
# fig.add_trace(go.Scatter(x=dates, y=equity_idx,
#     name='Equities', line=dict(color='#00C2A8')), row=1, col=1)
#
# fig.add_trace(go.Scatter(x=dates, y=bond_idx,
#     name='Bonds', line=dict(color='#3B82F6')), row=1, col=1)
#
# fig.update_layout(
#     title='Macro Intelligence Dashboard',
#     template='plotly_dark',
#     height=650
# )
# fig.show()

print("MACRO INTELLIGENCE DASHBOARD")
print("=" * 40)
print("Panel 1: Asset Performance (2019=100)")
print("Panel 2: Yield Curve Scenarios")
print("Panel 3: Rolling 12M Equity-Bond Correlation")
print("Panel 4: Annual Returns Comparison")
print("\\nDeploy with: streamlit run dashboard.py")`,
            financeApplication: 'Bloomberg terminals use interactive charts with hover details. Every modern portfolio dashboard is built with Plotly or similar tools. The multi-panel layout above is the standard format for macro intelligence dashboards at investment firms.',
            keyTakeaways: [
              'Plotly is the go-to for interactive financial dashboards',
              'Candlestick charts are the standard for price data \u2014 OHLC in one chart',
              'Multi-panel dashboards communicate complex stories in a single view',
              'Deploy dashboards with Streamlit for instant web applications'
            ],
            resources: [
              { title: 'Plotly Documentation', url: 'https://plotly.com/python/', type: 'docs' },
              { title: 'Streamlit Documentation', url: 'https://streamlit.io/', type: 'tool' }
            ]
          }
        },
        {
          icon: '\uD83C\uDFA8',
          title: 'Chart Selection & Design Principles',
          desc: 'Choosing the right chart for the right question. Signal vs. noise. How to present to executives.',
          tags: ['Design', 'Communication'],
          content: {
            overview: 'The difference between a good analyst and a great one is communication. Choosing the wrong chart type obscures your insight. A time series needs a line chart, not a bar chart. A distribution needs a histogram, not a pie chart. Executive audiences need the conclusion in the title, supporting evidence in the chart, and minimal decoration. Cole Nussbaumer Knaflic\'s framework: identify the message first, then choose the chart that delivers it.',
            keyConceptsList: [
              'Line chart: trends over time (prices, returns, growth)',
              'Bar chart: comparison across categories (sector performance, ratios)',
              'Scatter plot: relationship between two variables (risk vs return)',
              'Histogram: distribution shape (return distribution, VaR visualization)',
              'Heatmap: correlation matrices, factor exposures',
              'Title states the insight, not the data: "Tech outperforms by 12%" not "Sector Returns"'
            ],
            codeExample: `# Chart Selection Framework
chart_guide = {
    'Trend over time': 'Line chart (prices, cumulative returns)',
    'Compare categories': 'Bar chart (sector returns, ratios)',
    'Relationship': 'Scatter plot (risk vs return, PE vs growth)',
    'Distribution': 'Histogram (return distribution, VaR)',
    'Composition': 'Stacked bar or area (portfolio allocation)',
    'Correlation': 'Heatmap (factor correlation matrix)',
    'Price data': 'Candlestick (OHLC daily prices)',
}

print("CHART SELECTION FRAMEWORK")
print("=" * 50)
for question, chart in chart_guide.items():
    print(f"  {question:.<25} {chart}")

print("\\nDESIGN RULES:")
print("1. Title = the insight (not 'Figure 1')")
print("2. Remove gridlines, borders, 3D effects")
print("3. Use color with purpose (red=danger)")
print("4. Data-ink ratio: maximize data, minimize ink")
print("5. One message per chart")`,
            financeApplication: 'At Goldman Sachs, research analysts are trained to lead with the insight. At Bridgewater, every presentation follows the principle of "radical transparency" \u2014 the chart must convey the truth clearly. These design principles separate analyst-level work from VP-level presentations.',
            keyTakeaways: [
              'Choose chart type based on the question, not the data shape',
              'Title states the insight: "Tech outperforms by 12%" not "Sector Returns"',
              'Remove all chartjunk: no 3D, no gradients, no unnecessary gridlines',
              'One message per chart \u2014 if it has two messages, make two charts'
            ],
            resources: [
              { title: 'Storytelling with Data \u2014 Knaflic', url: 'https://www.storytellingwithdata.com/', type: 'book' }
            ]
          }
        }
      ],
      practiceProjects: [
        'Build a 4-panel macro intelligence dashboard with Plotly',
        'Create a portfolio performance report with properly designed charts',
        'Build an interactive candlestick chart with volume overlay'
      ]
    },

    // ─────────────────────────────────────────────────────────
    // STAGE 5 \u2014 Financial Modeling in Python (NEW)
    // ─────────────────────────────────────────────────────────
    {
      id: 5,
      stage: 'Stage 5 \u2014 Modeling',
      title: 'Financial Modeling in Python',
      duration: 'Weeks 25\u201334',
      dailyCommitment: '45 minutes',
      focus: 'Building institutional-grade financial models from scratch',
      philosophy: 'The spreadsheet communicates decisions. Python makes them. This stage bridges traditional finance and modern quantitative analysis \u2014 building models that would take weeks in Excel in hours with Python.',
      color: '#10B981',
      certification: 'FMVA \u2014 Corporate Finance Institute',
      lessons: [
        {
          icon: '\uD83C\uDFE6',
          title: 'DCF Valuation with Monte Carlo',
          desc: 'Build a full Discounted Cash Flow model with stochastic inputs and probability distribution of fair values.',
          tags: ['DCF', 'Valuation', 'Monte Carlo'],
          content: {
            overview: 'The DCF model answers the most fundamental question in finance: what is this business actually worth? In Python, we can go far beyond a spreadsheet DCF. By adding Monte Carlo simulation with stochastic inputs (randomized growth rates, margins, and discount rates), we generate a probability distribution of fair values instead of a single point estimate. This is how institutional investors actually think about valuation \u2014 in ranges, not absolutes.',
            keyConceptsList: [
              'DCF formula: Enterprise Value = PV of projected FCFs + PV of Terminal Value',
              'Terminal Value typically represents 60\u201380% of total DCF value \u2014 the most sensitive assumption',
              'WACC (discount rate) changes create massive valuation swings',
              'Monte Carlo: randomize inputs (growth, margins, WACC) to generate price distributions',
              'Scenario analysis: bear/base/bull cases with probability-weighted values',
              'Python classes encapsulate the model for reuse across different companies'
            ],
            codeExample: `import numpy as np

class DCFModel:
    def __init__(self, name, base_rev, cfg):
        self.name = name
        self.base_rev = base_rev
        self.cfg = cfg

    def deterministic(self):
        c = self.cfg
        rev, pv, fcf_last = self.base_rev, 0, 0
        for yr in range(1, c['years'] + 1):
            rev *= (1 + c['rev_growth'])
            fcf = (rev * c['ebit_margin'] * (1 - c['tax'])
                   ) - (rev * (c['capex'] - c['da']))
            pv += fcf / (1 + c['wacc'])**yr
            fcf_last = fcf
        tv = fcf_last * (1 + c['tg']) / (c['wacc'] - c['tg'])
        pv_tv = tv / (1 + c['wacc'])**c['years']
        price = (pv + pv_tv - c['net_debt']) / c['shares']
        print(f"Deterministic: \${price:.2f}/share")
        print(f"TV as % of EV: {pv_tv/(pv+pv_tv)*100:.1f}%")
        return price

    def monte_carlo(self, n=10_000):
        c = self.cfg
        prices = []
        for _ in range(n):
            rg = np.clip(np.random.normal(c['rev_growth'], 0.05), 0, 0.3)
            em = np.clip(np.random.normal(c['ebit_margin'], 0.03), 0.05, 0.3)
            wacc = np.clip(np.random.normal(c['wacc'], 0.015), 0.06, 0.20)
            tg = np.random.uniform(0.01, 0.03)
            rev, pv, fcf_last = self.base_rev, 0, 0
            for yr in range(1, c['years'] + 1):
                rev *= (1 + rg)
                fcf = rev * em * (1-c['tax']) - rev*(c['capex']-c['da'])
                pv += fcf / (1 + wacc)**yr
                fcf_last = fcf
            tv = fcf_last * (1+tg) / (wacc-tg)
            p = (pv + tv - c['net_debt']) / c['shares']
            if p > 0: prices.append(p)

        prices = np.array(prices)
        print(f"\\nMonte Carlo ({n:,} sims):")
        for pct in [5, 25, 50, 75, 95]:
            print(f"  P{pct:2d}: \${np.percentile(prices, pct):>8.2f}")

cfg = {
    'rev_growth': 0.15, 'ebit_margin': 0.22,
    'wacc': 0.10, 'tax': 0.21, 'tg': 0.025,
    'capex': 0.05, 'da': 0.04,
    'years': 5, 'net_debt': 2e9, 'shares': 500e6,
}
m = DCFModel("TechCo", 10e9, cfg)
m.deterministic()
m.monte_carlo()`,
            financeApplication: 'Every investment bank builds DCF models for M&A transactions. Every equity research analyst publishes price targets based on DCF. Adding Monte Carlo transforms a fragile point estimate into a robust probability distribution that honestly represents uncertainty.',
            keyTakeaways: [
              'DCF + Monte Carlo generates a distribution of fair values, not a single fragile estimate',
              'Terminal Value dominates total DCF value \u2014 test its sensitivity rigorously',
              'Small WACC changes create massive valuation swings \u2014 always scenario-test',
              'Python classes make the model reusable across hundreds of companies'
            ],
            resources: [
              { title: 'Financial Modeling & Valuation \u2014 Pignataro', url: 'https://www.amazon.com/Financial-Modeling-Valuation-Practical-Investment/dp/1118558766', type: 'book' },
              { title: 'Investment Banking \u2014 Rosenbaum & Pearl', url: 'https://www.amazon.com/Investment-Banking-Valuation-Financial-Modeling/dp/1119706181', type: 'book' }
            ]
          }
        },
        {
          icon: '\uD83D\uDCC9',
          title: 'Scenario Analysis & Sensitivity Tables',
          desc: 'Bear/base/bull automation, two-variable sensitivity tables, stress testing financial models.',
          tags: ['Scenarios', 'Sensitivity', 'Stress Test'],
          content: {
            overview: 'A single-point forecast is useless without understanding how it changes under different assumptions. Scenario analysis (bear/base/bull) and sensitivity tables (two-variable data tables) are how professionals stress-test models. In Python, you can automate what takes hours in Excel: computing a full sensitivity grid of valuations across different growth and discount rate assumptions in seconds.',
            keyConceptsList: [
              'Bear/base/bull scenarios assign probabilities to different economic outcomes',
              'Two-variable sensitivity tables show how valuation changes across two key inputs',
              'Stress testing: what happens under extreme assumptions (war, recession, pandemic)',
              'Probability-weighted fair value = sum of (scenario value \u00D7 probability)',
              'Automate scenario analysis to test hundreds of combinations in seconds'
            ],
            codeExample: `import numpy as np

# Simplified DCF for sensitivity analysis
def quick_dcf(rev, growth, margin, wacc, tg=0.025, years=5):
    pv, fcf = 0, 0
    for yr in range(1, years + 1):
        rev *= (1 + growth)
        fcf = rev * margin * 0.79  # After 21% tax
        pv += fcf / (1 + wacc)**yr
    tv = fcf * (1 + tg) / (wacc - tg)
    return pv + tv / (1 + wacc)**years

# Sensitivity table: Growth vs WACC
base_rev = 10e9
margin = 0.22
growths = [0.08, 0.10, 0.12, 0.15, 0.18, 0.20]
waccs = [0.08, 0.09, 0.10, 0.11, 0.12]

print("SENSITIVITY TABLE: Enterprise Value ($B)")
print(f"{'Growth':>8}", end='')
for w in waccs:
    print(f"  WACC {w:.0%}", end='')
print()
print("-" * 55)
for g in growths:
    print(f"  {g:.0%}  ", end='')
    for w in waccs:
        ev = quick_dcf(base_rev, g, margin, w) / 1e9
        print(f"  \${ev:>6.1f}B", end='')
    print()`,
            financeApplication: 'Every investment committee decision is backed by scenario analysis. The sensitivity table is the standard output from every DCF model at every investment bank. Automating this in Python lets you test hundreds of scenarios instead of the 3\u20135 that are practical in Excel.',
            keyTakeaways: [
              'Never present a single-point estimate without scenario analysis',
              'Two-variable sensitivity tables reveal which assumptions matter most',
              'Probability-weighted scenarios give a more honest fair value estimate',
              'Python automates what takes hours in Excel \u2014 test hundreds of scenarios in seconds'
            ],
            resources: [
              { title: 'Python for Finance \u2014 Yves Hilpisch', url: 'https://home.tpq.io/books/py4fi/', type: 'book' }
            ]
          }
        },
        {
          icon: '\uD83D\uDCB0',
          title: 'Capital Budgeting: NPV, IRR & Payback',
          desc: 'Automated project evaluation: which investments create value and which destroy it.',
          tags: ['NPV', 'IRR', 'Capital Budgeting'],
          content: {
            overview: 'Capital budgeting is how corporations decide which projects to fund. NPV (Net Present Value) is the gold standard: if NPV > 0, the project creates value. IRR (Internal Rate of Return) is the rate that makes NPV = 0. Payback period tells you when you get your money back. In Python, you can evaluate dozens of projects simultaneously and rank them by value creation.',
            keyConceptsList: [
              'NPV > 0: project creates value. NPV < 0: destroys value. Simple rule.',
              'IRR: the discount rate that makes NPV = 0 \u2014 compare to cost of capital (WACC)',
              'Payback Period: when cumulative cash flows turn positive \u2014 liquidity measure',
              'If IRR > WACC: project creates value (same conclusion as NPV > 0)',
              'Always prefer NPV when IRR and NPV conflict \u2014 NPV maximizes shareholder value'
            ],
            codeExample: `import numpy as np

def evaluate_project(name, cash_flows, wacc):
    """Evaluate a capital budgeting project."""
    # NPV
    npv = sum(cf / (1 + wacc)**t for t, cf in enumerate(cash_flows))

    # IRR (Newton's method approximation)
    irr_guess = 0.10
    for _ in range(100):
        npv_g = sum(cf / (1+irr_guess)**t for t,cf in enumerate(cash_flows))
        dnpv = sum(-t*cf / (1+irr_guess)**(t+1) for t,cf in enumerate(cash_flows))
        if abs(dnpv) < 1e-10: break
        irr_guess -= npv_g / dnpv

    # Payback
    cumulative = np.cumsum(cash_flows)
    payback = next((i for i, c in enumerate(cumulative) if c > 0), None)

    print(f"Project: {name}")
    print(f"  NPV:     \${npv:>12,.0f}  {'ACCEPT' if npv > 0 else 'REJECT'}")
    print(f"  IRR:     {irr_guess:>11.1%}  vs WACC {wacc:.1%}")
    print(f"  Payback: {payback} years")
    return npv

# Compare two projects
wacc = 0.10
evaluate_project("Factory Expansion",
    [-5_000_000, 1_200_000, 1_500_000, 1_800_000, 2_000_000, 2_200_000], wacc)
print()
evaluate_project("Software Platform",
    [-2_000_000, 400_000, 800_000, 1_200_000, 1_600_000, 2_000_000], wacc)`,
            financeApplication: 'Every CFO uses NPV/IRR to decide on factory expansions, acquisitions, R&D investments, and product launches. The framework above is exactly what runs in corporate finance departments worldwide \u2014 ranking projects by value creation per dollar invested.',
            keyTakeaways: [
              'NPV > 0 means the project creates shareholder value \u2014 the fundamental decision rule',
              'IRR vs WACC: if IRR exceeds cost of capital, the project earns more than it costs to fund',
              'Always prefer NPV when it conflicts with IRR \u2014 NPV maximizes total value',
              'Automate project evaluation to compare dozens of investment opportunities simultaneously'
            ],
            resources: [
              { title: 'Corporate Finance \u2014 Berk & DeMarzo', url: 'https://www.pearson.com/en-us/subject-catalog/p/corporate-finance/P200000005924', type: 'book' }
            ]
          }
        }
      ],
      practiceProjects: [
        'Build a DCF model with Monte Carlo for any public company',
        'Create a sensitivity table engine that takes any two inputs',
        'Build a capital budgeting tool that ranks projects by NPV/IRR'
      ]
    },

    // ─────────────────────────────────────────────────────────
    // STAGE 6 \u2014 Machine Learning for Finance
    // ─────────────────────────────────────────────────────────
    {
      id: 6,
      stage: 'Stage 6 \u2014 Machine Learning',
      title: 'Machine Learning for Finance',
      duration: 'Weeks 35\u201344',
      dailyCommitment: '45 minutes',
      focus: 'Applied ML for prediction, classification, and anomaly detection in finance',
      philosophy: 'ML is not magic. In finance, it is disciplined pattern recognition with rigorous out-of-sample validation. Most "alpha" discovered by ML is noise. The discipline is in knowing the difference.',
      color: '#EF4444',
      certification: 'Azure Data Scientist Associate / Google ML Engineer',
      lessons: [
        {
          icon: '\uD83E\uDD16',
          title: 'ML Landscape: When to Use What',
          desc: 'Supervised vs unsupervised, classification vs regression \u2014 choosing the right approach for each financial problem.',
          tags: ['ML Fundamentals', 'Decision Framework'],
          content: {
            overview: 'Machine learning in finance is about pattern recognition with discipline. The crucial first step is choosing the right approach. Predicting a continuous value (stock price, revenue)? That\'s regression. Classifying (fraud/not fraud, default/no default)? Classification. Finding hidden patterns (market regimes, customer segments)? Unsupervised learning. Most financial ML failures come from using the wrong technique, not from bad algorithms.',
            keyConceptsList: [
              'Supervised learning: labeled data \u2192 prediction (regression or classification)',
              'Regression: predict continuous values (revenue forecast, price target)',
              'Classification: predict categories (fraud detection, credit default, buy/sell signals)',
              'Unsupervised: no labels \u2014 clustering (market regimes), anomaly detection (fraud)',
              'Feature engineering matters more than algorithm choice in finance',
              'Backtesting discipline: walk-forward validation prevents look-ahead bias'
            ],
            codeExample: `# ML Decision Framework for Finance
problems = {
    'Revenue Forecasting': 'Regression (Gradient Boosting)',
    'Credit Default': 'Classification (Logistic Reg / XGBoost)',
    'Fraud Detection': 'Anomaly Detection (Isolation Forest)',
    'Market Regimes': 'Clustering (K-Means / HMM)',
    'Customer Segments': 'Clustering (K-Means / DBSCAN)',
    'Stock Selection': 'Ranking (Learning to Rank / RF)',
    'Sentiment Analysis': 'Classification (NLP / BERT)',
    'Option Pricing': 'Regression (Neural Networks)',
}

print("ML DECISION FRAMEWORK FOR FINANCE")
print("=" * 55)
for problem, approach in problems.items():
    print(f"  {problem:.<30} {approach}")

print("\\nCRITICAL RULES:")
print("1. Feature engineering > algorithm choice")
print("2. Walk-forward validation (no look-ahead)")
print("3. Simple models first (logistic reg before neural nets)")
print("4. If you can't explain it, don't trade on it")`,
            financeApplication: 'Every quant fund has an ML decision framework. Renaissance Technologies and Two Sigma use the full spectrum of ML approaches. The key differentiator between firms that succeed and fail with ML is discipline: proper validation, avoiding overfitting, and understanding that most discovered "signals" are noise.',
            keyTakeaways: [
              'Choose the ML approach based on the problem type, not the buzzword',
              'Feature engineering (creating useful inputs) matters more than algorithm choice',
              'Walk-forward validation is mandatory in finance \u2014 prevents look-ahead bias',
              'Simple models often beat complex ones in noisy financial data'
            ],
            resources: [
              { title: 'Advances in Financial ML \u2014 de Prado', url: 'https://www.amazon.com/Advances-Financial-Machine-Learning-Marcos/dp/1119482089', type: 'book' },
              { title: 'Hands-On ML \u2014 G\u00E9ron', url: 'https://www.oreilly.com/library/view/hands-on-machine-learning/9781098125967/', type: 'book' }
            ]
          }
        },
        {
          icon: '\uD83D\uDCC8',
          title: 'Walk-Forward Revenue Forecasting',
          desc: 'Gradient boosting with proper time series cross-validation \u2014 no look-ahead bias, production-ready.',
          tags: ['Time Series ML', 'Gradient Boosting'],
          content: {
            overview: 'The most common ML mistake in finance: training and testing on the same time period, or allowing future data to leak into training. Walk-forward validation solves this: train on historical data, predict the next period, slide forward, repeat. This produces honest out-of-sample error estimates. Gradient Boosting (XGBoost, LightGBM) is the workhorse algorithm for tabular financial data.',
            keyConceptsList: [
              'Walk-forward validation: train on past, predict future, slide window forward',
              'No look-ahead bias: features must only use data available at prediction time',
              'Feature engineering for time series: lags, rolling means, seasonal indicators',
              'Gradient Boosting: ensemble of decision trees, excellent for tabular financial data',
              'MAPE (Mean Absolute Percentage Error): the standard metric for financial forecasts',
              'Feature importance reveals which factors drive predictions \u2014 essential for trust'
            ],
            codeExample: `import numpy as np
import pandas as pd
from sklearn.ensemble import GradientBoostingRegressor
from sklearn.metrics import mean_absolute_percentage_error

np.random.seed(42)

# Simulated monthly revenue with trend + seasonality
months = pd.date_range('2018-01-01', '2024-12-31', freq='MS')
n = len(months)
t = np.arange(n)
revenue = np.linspace(10, 25, n) * 1e6 * (
    1 + 0.15*np.sin(2*np.pi*t/12) + np.random.normal(0, 0.05, n))

df = pd.DataFrame({'Date': months, 'Revenue': revenue})
df['Month'] = df['Date'].dt.month

# Feature engineering (NO look-ahead)
for lag in [1, 3, 6, 12]:
    df[f'lag_{lag}'] = df['Revenue'].shift(lag)
for w in [3, 6, 12]:
    df[f'roll_mean_{w}'] = df['Revenue'].shift(1).rolling(w).mean()
df = df.dropna()

features = [c for c in df.columns if c not in ['Date', 'Revenue']]

# Walk-forward validation
print("Walk-Forward Revenue Forecast:")
results = []
for i in range(36, len(df)-6, 6):
    train, test = df.iloc[:i], df.iloc[i:i+6]
    model = GradientBoostingRegressor(n_estimators=100, max_depth=3)
    model.fit(train[features], train['Revenue'])
    preds = model.predict(test[features])
    mape = mean_absolute_percentage_error(test['Revenue'], preds)*100
    print(f"  {test['Date'].iloc[0].strftime('%Y-%m')}: MAPE={mape:.1f}%")
    results.extend(zip(test['Revenue'], preds))

overall = np.mean([abs(a-p)/a for a,p in results]) * 100
print(f"\\nOverall MAPE: {overall:.1f}% (good: 5-15%)")`,
            financeApplication: 'Every budgeting cycle at every corporation involves revenue forecasting. Hedge funds use similar walk-forward approaches for trading signal generation. The walk-forward discipline is what separates backtested fantasies from production-ready models.',
            keyTakeaways: [
              'Walk-forward validation is mandatory \u2014 standard train/test splits don\'t work for time series',
              'Features must only use data available at prediction time \u2014 no future leakage',
              'Gradient Boosting is the workhorse for tabular financial data',
              'MAPE of 5\u201315% is considered good for financial forecasting'
            ],
            resources: [
              { title: 'Machine Learning for Asset Managers \u2014 de Prado', url: 'https://www.cambridge.org/core/elements/machine-learning-for-asset-managers/6D9211305EA2095BE3B50FF6C4F83C6D', type: 'book' }
            ]
          }
        },
        {
          icon: '\uD83D\uDD0E',
          title: 'Anomaly Detection & Fraud Monitoring',
          desc: 'Isolation Forest, statistical outlier detection \u2014 finding the transactions that don\'t belong.',
          tags: ['Anomaly Detection', 'Fraud', 'Unsupervised'],
          content: {
            overview: 'Fraud costs financial institutions $30B+ annually. Traditional rules-based systems catch known patterns but miss novel fraud. Isolation Forest works differently: it isolates outliers by randomly partitioning data, based on the insight that anomalies are easier to isolate than normal points. Combined with statistical methods (z-scores, Mahalanobis distance), it forms a powerful fraud detection system.',
            keyConceptsList: [
              'Isolation Forest: anomalies are easier to isolate than normal points',
              'The algorithm assigns anomaly scores from -1 (anomaly) to +1 (normal)',
              'contamination parameter: expected proportion of anomalies (0.01\u20130.05 for fraud)',
              'Combine with domain rules: unusually large transactions, new geographies, velocity',
              'False positive management: too many alerts = alert fatigue = missed real fraud',
              'Feature engineering: transaction amount, frequency, time patterns, deviation from history'
            ],
            codeExample: `import numpy as np
from sklearn.ensemble import IsolationForest

np.random.seed(42)

# Normal transactions
n_normal = 1000
amounts = np.random.lognormal(mean=4, sigma=1.2, size=n_normal)
freq = np.random.poisson(lam=5, size=n_normal)

# Inject anomalies (fraud)
n_fraud = 20
fraud_amounts = np.random.uniform(10000, 50000, n_fraud)
fraud_freq = np.random.uniform(15, 30, n_fraud)

X = np.column_stack([
    np.concatenate([amounts, fraud_amounts]),
    np.concatenate([freq, fraud_freq])
])

# Isolation Forest
clf = IsolationForest(contamination=0.03, random_state=42)
preds = clf.fit_predict(X)

n_flagged = (preds == -1).sum()
fraud_caught = (preds[-n_fraud:] == -1).sum()

print("FRAUD DETECTION RESULTS")
print("=" * 40)
print(f"Total transactions: {len(X):,}")
print(f"Flagged as anomaly: {n_flagged}")
print(f"Actual fraud:       {n_fraud}")
print(f"Fraud caught:       {fraud_caught}/{n_fraud}")
print(f"Detection rate:     {fraud_caught/n_fraud:.0%}")
print(f"False positives:    {n_flagged - fraud_caught}")`,
            financeApplication: 'Every bank and payment processor runs anomaly detection. Visa processes 150M+ transactions daily and flags suspicious ones in real-time. Compliance teams use similar models to detect insider trading, money laundering, and market manipulation.',
            keyTakeaways: [
              'Isolation Forest is the go-to algorithm for financial anomaly detection',
              'Combine ML with domain-specific rules for best results',
              'Balance detection rate vs false positives \u2014 too many alerts = missed real fraud',
              'Feature engineering from transaction patterns is more important than algorithm choice'
            ],
            resources: [
              { title: 'Scikit-learn Isolation Forest', url: 'https://scikit-learn.org/stable/modules/generated/sklearn.ensemble.IsolationForest.html', type: 'docs' }
            ]
          }
        }
      ],
      practiceProjects: [
        'Build a walk-forward revenue forecasting model for a public company',
        'Create a fraud detection system using Isolation Forest on transaction data',
        'Implement a stock clustering model to identify market regime changes'
      ]
    },

    // ─────────────────────────────────────────────────────────
    // STAGE 7 \u2014 Macro Factor Modeling (NEW)
    // ─────────────────────────────────────────────────────────
    {
      id: 7,
      stage: 'Stage 7 \u2014 Macro Strategy',
      title: 'Macro Factor Modeling & Systematic Strategy',
      duration: 'Weeks 45\u201356',
      dailyCommitment: '60 minutes',
      focus: 'Building the analytical engine behind systematic macro funds',
      philosophy: 'Ray Dalio built Bridgewater on one insight: economies are machines driven by identifiable forces. If you understand the machine, you can anticipate its output. This stage teaches you to build that understanding in Python.',
      color: '#2F9731',
      certification: 'CFA Level II (Portfolio Management)',
      lessons: [
        {
          icon: '\uD83C\uDF0D',
          title: 'The Bridgewater Framework: Regime Detection',
          desc: 'Four-quadrant regime classifier (growth \u00D7 inflation) with asset allocation rules for each environment.',
          tags: ['Macro', 'Regimes', 'Bridgewater'],
          content: {
            overview: 'Ray Dalio\'s framework decomposes the economic environment into four quadrants based on two axes: growth (rising/falling) and inflation (rising/falling). Each quadrant has different asset class winners. Equities thrive when growth rises. Bonds win when growth and inflation fall. Gold and commodities protect when inflation rises. By classifying the current regime, you can systematically tilt your portfolio toward assets favored by the environment.',
            keyConceptsList: [
              'Four quadrants: Growth\u2191 Inflation\u2191 (hot), Growth\u2191 Inflation\u2193 (Goldilocks), Growth\u2193 Inflation\u2191 (Stagflation), Growth\u2193 Inflation\u2193 (Deflation)',
              'Each regime has "favored" and "unfavored" asset classes',
              'Regime detection uses momentum of growth and inflation indicators',
              'GDP, PMI, employment data indicate growth direction',
              'CPI, PPI, breakeven inflation rates indicate inflation direction',
              'The All-Weather approach: allocate to all quadrants simultaneously'
            ],
            codeExample: `import numpy as np
import pandas as pd

REGIME_MAP = {
    'GR_IR': {'name': 'Goldilocks-Hot',
              'favored': ['Equities','Commodities','TIPS'],
              'unfavored': ['Long_Bonds','Cash']},
    'GR_IF': {'name': 'Goldilocks',
              'favored': ['Equities','Corp_Bonds','Real_Estate'],
              'unfavored': ['Commodities','Gold']},
    'GF_IR': {'name': 'Stagflation',
              'favored': ['Gold','TIPS','Commodities','Cash'],
              'unfavored': ['Equities','Long_Bonds']},
    'GF_IF': {'name': 'Deflationary Bust',
              'favored': ['Long_Bonds','Gold','Cash'],
              'unfavored': ['Equities','Commodities']},
}

def classify(g_mom, i_mom):
    key = f"{'GR' if g_mom > 0 else 'GF'}_{'IR' if i_mom > 0 else 'IF'}"
    return REGIME_MAP[key]

def allocation(regime):
    nf = len(regime['favored'])
    nu = len(regime['unfavored'])
    alloc = {}
    for a in regime['favored']:
        alloc[a] = round(0.75 / nf, 3)
    for a in regime['unfavored']:
        alloc[a] = round(0.25 / nu, 3)
    return alloc

# Simulate regime history
np.random.seed(42)
dates = pd.date_range('2010-01-01', '2024-12-31', freq='QS')
t = np.linspace(0, 4*np.pi, len(dates))
g = np.sin(t) + np.random.normal(0, 0.3, len(dates))
i_data = np.sin(t + np.pi/3) + np.random.normal(0, 0.25, len(dates))

history = [classify(gv, iv)['name'] for gv, iv in zip(g, i_data)]
regime_counts = pd.Series(history).value_counts()

print("MACRO REGIME DISTRIBUTION (2010-2024)")
print("=" * 42)
for regime, cnt in regime_counts.items():
    pct = cnt / len(history) * 100
    bar = "#" * int(pct / 2)
    print(f"  {regime:<22} {bar} {pct:.0f}%")

cur = classify(g[-1], i_data[-1])
alloc = allocation(cur)
print(f"\\nCURRENT: {cur['name']}")
for asset, w in sorted(alloc.items(), key=lambda x: -x[1]):
    print(f"  {asset:<20} {w*100:.1f}%")`,
            financeApplication: 'This is a simplified version of how Bridgewater\'s $150B All Weather fund allocates capital. Every systematic macro fund uses some form of regime classification to determine which asset classes to overweight. The framework above is implementable by any investor.',
            keyTakeaways: [
              'Four economic quadrants drive asset class performance: growth \u00D7 inflation',
              'Each regime has winners and losers \u2014 systematic allocation tilts toward winners',
              'The All-Weather approach: allocate to all quadrants so you\'re never fully wrong',
              'Regime detection using growth and inflation momentum is implementable by anyone'
            ],
            resources: [
              { title: 'Principles for Big Debt Crises \u2014 Dalio (free PDF)', url: 'https://www.principles.com/big-debt-crises/', type: 'book' },
              { title: 'Expected Returns \u2014 Antti Ilmanen', url: 'https://www.amazon.com/Expected-Returns-Investors-Harvesting-Rewards/dp/1119990726', type: 'book' }
            ]
          }
        },
        {
          icon: '\uD83D\uDCC9',
          title: 'Yield Curve Analysis & Recession Prediction',
          desc: 'Building a yield curve model: inversion detection, steepening analysis, and recession probability estimation.',
          tags: ['Yield Curve', 'Macro', 'Prediction'],
          content: {
            overview: 'The yield curve has predicted every US recession since 1955. When short-term rates exceed long-term rates (inversion), a recession typically follows within 12\u201318 months. In Python, we can build a real-time yield curve monitor that tracks the 2Y\u201310Y spread, detects inversions, and estimates recession probability based on historical patterns. This is one of the most reliable \u2014 and most overlooked \u2014 signals in finance.',
            keyConceptsList: [
              'Normal yield curve: long rates > short rates (healthy economy)',
              'Inverted yield curve: short rates > long rates (recession warning)',
              '2Y\u201310Y spread: the most watched yield curve metric',
              'Every US recession since 1955 was preceded by yield curve inversion',
              'Average lead time: 12\u201318 months from inversion to recession',
              'Bull steepening (short rates fall) = Fed easing. Bear steepening (long rates rise) = inflation fears'
            ],
            codeExample: `import numpy as np

# Yield curve scenarios
maturities = ['1M','3M','6M','1Y','2Y','3Y','5Y','10Y','30Y']

normal = [4.8, 4.9, 5.0, 4.9, 4.5, 4.3, 4.1, 3.9, 4.1]
inverted = [5.4, 5.5, 5.5, 5.4, 5.0, 4.7, 4.3, 3.9, 4.1]

# 2Y-10Y spread analysis
spread_normal = normal[7] - normal[4]  # 10Y - 2Y
spread_inverted = inverted[7] - inverted[4]

print("YIELD CURVE ANALYSIS")
print("=" * 45)
print(f"{'Maturity':>8} {'Normal':>8} {'Inverted':>10}")
print("-" * 30)
for m, n, i in zip(maturities, normal, inverted):
    print(f"{m:>8} {n:>7.1f}% {i:>9.1f}%")

print(f"\\n2Y-10Y Spread:")
print(f"  Normal:   {spread_normal:+.1f}% (healthy)")
print(f"  Inverted: {spread_inverted:+.1f}% (WARNING)")
print(f"\\nRecession track record:")
print(f"  Predicted every US recession since 1955")
print(f"  Average lead time: 12-18 months")
print(f"  False positives: very rare")`,
            financeApplication: 'Every fixed income desk monitors the yield curve in real-time. The 2Y\u201310Y inversion preceded the 2020 recession, the 2008 crisis, the 2001 recession, and every other downturn since 1955. Central bank policy transmission works through the yield curve \u2014 understanding it is understanding the monetary system.',
            keyTakeaways: [
              'Yield curve inversion is the most reliable recession predictor available',
              'The 2Y\u201310Y spread is the most watched metric \u2014 negative = recession warning',
              'Average lead time from inversion to recession: 12\u201318 months',
              'Understanding the yield curve means understanding the monetary system'
            ],
            resources: [
              { title: 'Macro Trading Strategies \u2014 Burstein', url: 'https://www.amazon.com/Macro-Trading-Investment-Strategies-Macroeconomic/dp/0471315869', type: 'book' }
            ]
          }
        }
      ],
      practiceProjects: [
        'Build a real-time macro regime classifier using economic indicators',
        'Create a yield curve monitor that detects inversions and estimates recession probability',
        'Implement a systematic asset allocation engine based on macro regimes'
      ]
    },

    // ─────────────────────────────────────────────────────────
    // STAGE 8 \u2014 Risk Parity & Portfolio Construction (NEW)
    // ─────────────────────────────────────────────────────────
    {
      id: 8,
      stage: 'Stage 8 \u2014 Portfolio Construction',
      title: 'Risk Parity, All-Weather & Portfolio Construction',
      duration: 'Weeks 57\u201372',
      dailyCommitment: '60 minutes',
      focus: 'Build a systematic, all-weather investment process from scratch',
      philosophy: 'Most portfolios are not diversified \u2014 they are equity portfolios with decoration. A 60/40 portfolio has 90% of its risk in equities. Risk parity fixes this by equalizing risk contribution from each asset class, creating genuine diversification.',
      color: '#A78BFA',
      certification: 'CFA Level III / FRM \u2014 Financial Risk Manager',
      lessons: [
        {
          icon: '\u2696\uFE0F',
          title: 'Risk Parity: Equal Risk, Not Equal Dollars',
          desc: 'The mathematical framework behind Bridgewater\'s All Weather \u2014 allocating by risk contribution, not capital.',
          tags: ['Risk Parity', 'All-Weather', 'Portfolio'],
          content: {
            overview: 'Traditional 60/40 portfolios allocate 60% to equities and 40% to bonds by capital. But equities are 3\u20134\u00D7 more volatile than bonds, so ~90% of the portfolio\'s risk comes from equities. The "40% in bonds" provides almost no diversification benefit. Risk parity solves this by allocating so that each asset contributes equally to portfolio risk. The result: genuine diversification, smoother returns, and better risk-adjusted performance across all economic environments.',
            keyConceptsList: [
              'Traditional 60/40: 60% capital in equities but ~90% risk from equities',
              'Risk parity: equalize risk contribution, not capital allocation',
              'Risk contribution = weight \u00D7 (covariance with portfolio) / portfolio volatility',
              'Volatility targeting: adjust leverage to hit a target volatility (e.g., 10%)',
              'Lower-volatility assets get higher weights to equalize risk contributions',
              'The optimization minimizes the difference between each asset\'s risk contribution'
            ],
            codeExample: `import numpy as np
from scipy.optimize import minimize

assets = ['US_Equity','LT_Bonds','IT_Bonds','Gold','Commodities']

# Simulated annual covariance matrix
np.random.seed(42)
vols = np.array([0.16, 0.12, 0.06, 0.14, 0.18])
corr = np.array([
    [1.0, -0.3, -0.2, 0.05, 0.2],
    [-0.3, 1.0, 0.8, 0.25, -0.1],
    [-0.2, 0.8, 1.0, 0.15, -0.05],
    [0.05, 0.25, 0.15, 1.0, 0.15],
    [0.2, -0.1, -0.05, 0.15, 1.0]
])
cov = np.diag(vols) @ corr @ np.diag(vols)

def risk_parity_weights(cov):
    n = len(cov)
    def obj(w):
        pv = np.sqrt(w @ cov @ w)
        rc = w * (cov @ w) / pv
        return np.sum((rc - pv/n)**2)
    w0 = np.ones(n) / n
    bounds = [(0.01, 0.60)] * n
    cons = {'type': 'eq', 'fun': lambda w: np.sum(w) - 1}
    res = minimize(obj, w0, method='SLSQP', bounds=bounds, constraints=cons)
    return res.x

rp_w = risk_parity_weights(cov)
pv = np.sqrt(rp_w @ cov @ rp_w)
rc = rp_w * (cov @ rp_w) / pv

print("ALL-WEATHER RISK PARITY")
print("=" * 48)
for a, w, r in zip(assets, rp_w, rc/rc.sum()):
    print(f"  {a:<18} Weight:{w*100:5.1f}%  Risk:{r*100:5.1f}%")

# Compare with 60/40
w_6040 = np.array([0.60, 0.40, 0.0, 0.0, 0.0])
rc_6040 = w_6040 * (cov @ w_6040) / np.sqrt(w_6040 @ cov @ w_6040)
print(f"\\n60/40 RISK DECOMPOSITION:")
for a, r in zip(assets, rc_6040/rc_6040.sum()):
    if r > 0.01:
        print(f"  {a:<18} Risk:{r*100:5.1f}%")
print("  -> 60/40 has ~90% risk from equities alone")`,
            financeApplication: 'This is the core algorithm behind Bridgewater\'s All Weather fund ($150B+ AUM). Risk parity is also used by AQR, Man Group, and dozens of other systematic funds. The insight \u2014 that traditional portfolios are dramatically under-diversified from a risk perspective \u2014 has transformed institutional portfolio construction.',
            keyTakeaways: [
              '60/40 portfolios have ~90% of risk from equities \u2014 not actually diversified',
              'Risk parity equalizes risk contributions, creating genuine diversification',
              'Lower-volatility assets (bonds) get higher weights to balance risk',
              'This is the core algorithm behind Bridgewater\'s $150B All Weather fund'
            ],
            resources: [
              { title: 'Principles \u2014 Ray Dalio', url: 'https://www.principles.com/', type: 'book' },
              { title: 'Active Portfolio Management \u2014 Grinold & Kahn', url: 'https://www.amazon.com/Active-Portfolio-Management-Quantitative-Controlling/dp/0070248826', type: 'book' }
            ]
          }
        },
        {
          icon: '\uD83D\uDCC8',
          title: 'Walk-Forward Backtesting Framework',
          desc: 'Building a rigorous backtesting engine that honestly measures strategy performance without overfitting.',
          tags: ['Backtesting', 'Validation', 'Production'],
          content: {
            overview: 'Most backtested strategies fail in production because they\'re overfit to historical data. A proper walk-forward backtest rebalances periodically using only data available at each point, never looking ahead. Transaction costs, slippage, and realistic execution must be modeled. The backtest is the last line of defense before deploying real capital \u2014 get it wrong and you\'re flying blind.',
            keyConceptsList: [
              'Walk-forward: at each rebalance date, use only past data \u2014 never future',
              'Rebalance frequency: monthly, quarterly, or threshold-based (when weights drift)',
              'Transaction costs: model round-trip costs (commissions + spread + market impact)',
              'Performance metrics: annualized return, volatility, Sharpe ratio, max drawdown',
              'Benchmark comparison: beat a simple 60/40 or all-equity portfolio?',
              'Out-of-sample only: the only honest test is data the model has never seen'
            ],
            codeExample: `import numpy as np
import pandas as pd

np.random.seed(42)
n = 252 * 10  # 10 years daily
dates = pd.date_range('2015-01-01', periods=n, freq='B')

# Simulated multi-asset returns
assets = ['Equity', 'Bonds', 'Gold', 'Commodities']
mus = [0.0004, 0.0001, 0.0002, 0.0001]
vols = [0.012, 0.004, 0.009, 0.014]
returns = pd.DataFrame({
    a: np.random.normal(m, v, n)
    for a, m, v in zip(assets, mus, vols)
}, index=dates)

# Simple equal-weight backtest with quarterly rebalancing
weights = np.array([0.25, 0.25, 0.25, 0.25])
port_returns = (returns * weights).sum(axis=1)

# Performance metrics
ann_ret = port_returns.mean() * 252
ann_vol = port_returns.std() * np.sqrt(252)
sharpe = ann_ret / ann_vol
cum = (1 + port_returns).cumprod()
dd = (cum / cum.cummax() - 1).min()

print("BACKTEST RESULTS (10 YEARS)")
print("=" * 40)
print(f"Ann. Return:     {ann_ret*100:.2f}%")
print(f"Ann. Volatility: {ann_vol*100:.2f}%")
print(f"Sharpe Ratio:    {sharpe:.3f}")
print(f"Max Drawdown:    {dd*100:.2f}%")
print(f"Final Value:     \${cum.iloc[-1]*1e6:,.0f} (on $1M)")`,
            financeApplication: 'Every quant fund backtests strategies before deploying capital. Marcos L\u00F3pez de Prado exposed that most published trading strategies are false discoveries due to overfitting. The walk-forward framework above is the antidote \u2014 testing honestly on data the model has never seen.',
            keyTakeaways: [
              'Walk-forward backtesting is the only honest way to evaluate a strategy',
              'Model transaction costs, slippage, and realistic execution assumptions',
              'Most backtested strategies fail in production due to overfitting \u2014 be skeptical',
              'The Sharpe ratio and max drawdown are the two metrics that matter most'
            ],
            resources: [
              { title: 'Systematic Trading \u2014 Robert Carver', url: 'https://www.amazon.com/Systematic-Trading-designing-trading-investing/dp/0857194453', type: 'book' },
              { title: 'Advances in Financial ML \u2014 de Prado', url: 'https://www.amazon.com/Advances-Financial-Machine-Learning-Marcos/dp/1119482089', type: 'book' }
            ]
          }
        }
      ],
      practiceProjects: [
        'Build a full risk parity portfolio optimizer with volatility targeting',
        'Create a walk-forward backtesting engine that compares risk parity vs 60/40 over 10 years',
        'Implement a systematic rebalancing engine with transaction cost modeling'
      ]
    }
  ],

  // ═══════════════════════════════════════════════════════════
  // MASTER CLASS \u2014 Industry Legends
  // ═══════════════════════════════════════════════════════════
  masterClass: {
    title: 'Learn from the Masters',
    subtitle: 'Insights from Industry Legends',
    insights: [
      {
        icon: '\uD83C\uDF0A',
        name: 'Ray Dalio',
        role: 'Founder of Bridgewater Associates ($150B AUM)',
        title: 'The Economic Machine',
        text: 'Dalio sees the economy as a simple machine driven by transactions, credit, and productivity. His framework: short-term debt cycle (5\u20138 years), long-term debt cycle (75\u2013100 years), and productivity growth. Understanding the machine means you can anticipate outcomes.',
        principles: [
          'Diversification is the holy grail \u2014 find 15+ uncorrelated return streams',
          'Pain + Reflection = Progress \u2014 keep an investment journal',
          'Radical transparency: write down beliefs so they can be tested',
          'Most portfolios are not diversified \u2014 they are equity portfolios with decoration'
        ]
      },
      {
        icon: '\uD83D\uDCCA',
        name: 'Wes McKinney',
        role: 'Creator of Pandas, Author of "Python for Data Analysis"',
        title: 'The Tool That Changed Finance',
        text: 'McKinney created Pandas in 2008 at AQR Capital Management because existing tools couldn\'t handle the data manipulation needs of quantitative finance. Today, virtually every quant desk runs on Pandas.',
        principles: [
          'Method chaining produces readable, maintainable code',
          'Vectorization over loops \u2014 let NumPy/Pandas handle iteration',
          'DatetimeIndex is non-negotiable for financial time series',
          'Test your data pipeline as rigorously as your models'
        ]
      },
      {
        icon: '\uD83D\uDD2C',
        name: 'Jake VanderPlas',
        role: 'Author of Python Data Science Handbook',
        title: 'The Scientific Method for Data',
        text: 'VanderPlas emphasizes treating data science as science: formulate hypotheses, test rigorously, document everything. In finance, this means backtesting strategies properly and never mistaking correlation for causation.',
        principles: [
          'Always split data into train/test sets',
          'Visualize before you model \u2014 charts reveal what statistics hide',
          'Simple models often beat complex ones in noisy data',
          'Reproducibility is non-negotiable in financial research'
        ]
      },
      {
        icon: '\uD83E\uDD16',
        name: 'Marcos L\u00F3pez de Prado',
        role: 'Head of ML at AQR, Author of "Advances in Financial ML"',
        title: 'The Dangers of Backtesting',
        text: 'De Prado exposed how most published trading strategies are false discoveries due to multiple testing. His work revolutionized how quant funds validate strategies \u2014 introducing purged cross-validation and combinatorial backtesting.',
        principles: [
          'Most backtested strategies fail in production \u2014 be deeply skeptical',
          'Use purged cross-validation for financial time series',
          'Feature importance often misleads \u2014 use SHAP values instead',
          'The biggest risk in ML finance is overfitting, not underfitting'
        ]
      },
      {
        icon: '\uD83D\uDCC8',
        name: 'Andrew Ng',
        role: 'Co-founder of Coursera, AI Pioneer',
        title: 'The Practical AI Mindset',
        text: 'Ng democratized ML education. His advice for financial applications: start with the problem, not the algorithm. Most business value comes from simple models well-deployed, not complex models stuck in research.',
        principles: [
          'Data quality trumps algorithm complexity every time',
          'Start simple, add complexity only when justified',
          'Iteration speed beats initial accuracy \u2014 deploy early, learn fast',
          'The best model is the one that ships to production'
        ]
      }
    ]
  },

  // ═══════════════════════════════════════════════════════════
  // GLOSSARY
  // ═══════════════════════════════════════════════════════════
  glossary: [
    { term: 'DataFrame', def: 'Pandas 2D data structure \u2014 like a supercharged Excel sheet in Python.' },
    { term: 'Vectorization', def: 'Operating on entire arrays at once instead of looping \u2014 50\u2013100\u00D7 faster.' },
    { term: 'Feature Engineering', def: 'Creating new variables from raw data to improve model performance.' },
    { term: 'Overfitting', def: 'Model memorizes training data but fails on new data \u2014 the #1 ML risk in finance.' },
    { term: 'Walk-Forward', def: 'Backtesting method that only uses past data at each step \u2014 prevents look-ahead bias.' },
    { term: 'VaR', def: 'Value at Risk \u2014 the loss not exceeded at a given confidence level (e.g., 95%).' },
    { term: 'CVaR', def: 'Conditional VaR (Expected Shortfall) \u2014 the average loss when VaR is breached.' },
    { term: 'Sharpe Ratio', def: 'Return per unit of risk: (Return \u2212 Risk-Free) \u00F7 Volatility. 1.0 is good, 2.0+ excellent.' },
    { term: 'Risk Parity', def: 'Allocating so each asset contributes equally to portfolio risk, not equal capital.' },
    { term: 'DCF', def: 'Discounted Cash Flow \u2014 valuation by projecting and discounting future free cash flows.' },
    { term: 'WACC', def: 'Weighted Average Cost of Capital \u2014 the discount rate used in DCF models.' },
    { term: 'Alpha', def: 'Return above the market benchmark after adjusting for risk \u2014 the holy grail of active management.' },
    { term: 'Beta', def: 'Sensitivity to market movements. Beta of 1.5 means 1.5\u00D7 the market\'s movement.' },
    { term: 'Monte Carlo', def: 'Simulation generating thousands of random scenarios to quantify uncertainty.' },
    { term: 'MAPE', def: 'Mean Absolute Percentage Error \u2014 standard forecast accuracy metric (5\u201315% is good).' },
    { term: 'Isolation Forest', def: 'ML algorithm for anomaly detection \u2014 isolates outliers by random partitioning.' }
  ],

  // ═══════════════════════════════════════════════════════════
  // QUANT TECH STACK
  // ═══════════════════════════════════════════════════════════
  sampleAnalysis: {
    title: 'The Quant Finance Tech Stack',
    description: 'What tools the best data scientists use',
    stack: [
      { tool: 'Python', usage: '95%', desc: 'Primary language for data science and quant finance', color: '#3776ab' },
      { tool: 'Pandas', usage: '95%', desc: 'Data manipulation and financial time series', color: '#150458' },
      { tool: 'NumPy', usage: '90%', desc: 'Numerical computing and vectorized math', color: '#013243' },
      { tool: 'Scikit-learn', usage: '80%', desc: 'Machine learning for classification, regression, clustering', color: '#f89939' },
      { tool: 'Plotly', usage: '75%', desc: 'Interactive dashboards and financial charts', color: '#3f4f75' },
      { tool: 'SciPy', usage: '70%', desc: 'Optimization, statistics, and scientific computing', color: '#0054a6' }
    ]
  }
};

// AI Tutor system message
export const AI_TUTOR_SYSTEM_MESSAGE = `You are a seasoned quantitative investor and educator integrated into StudyCoach Academy. You combine deep Python expertise with institutional finance knowledge.

Your approach:
- Connect theory to practice using Bridgewater mental models
- Explain the financial intuition BEFORE the code syntax
- Challenge learners to think probabilistically and in systems
- Be direct, precise, and intellectually honest
- Reference real firms, real trades, real outcomes

You know: Python, Pandas, NumPy, scikit-learn, statsmodels, Plotly, SciPy.
You understand: DCF, CAPM, factor models, risk parity, macro regimes, portfolio construction.

Students are following an 18-month roadmap from Python basics to systematic portfolio construction. Keep responses concise (2\u20134 paragraphs) unless asked for more detail.`;
